import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtCurrency, ASSET_META } from "@/lib/format";
import { TrendChart, AllocationPie } from "@/components/charts";

export default function AssetsSummary() {
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, snap] = await Promise.all([
        api.get("/assets/summary"),
        api.get("/assets/snapshots"),
      ]);
      setSummary(s.data);
      setSnapshots(snap.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !summary) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const cur = summary.base_currency;
  const allocation = Object.entries(summary.by_type).map(([type, value]) => ({
    name: ASSET_META[type].label,
    value,
    color: ASSET_META[type].color,
  }));
  const commodities = summary.holdings
    .filter((h) => h.type === "commodities" && h.value != null)
    .map((h, i) => ({ name: h.name, value: h.value, color: ["#D6C3A5", "#c9b28c", "#e0d1b5", "#b89f72"][i % 4] }));

  return (
    <div className="space-y-8" data-testid="assets-summary">
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Total net worth</p>
        <p className="font-serif text-4xl md:text-5xl text-foreground" data-testid="net-worth-total">
          {fmtCurrency(summary.total, cur)}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {Object.entries(summary.by_type).map(([type, value]) => (
          <div key={type} className="rounded-xl border border-border bg-card p-4" data-testid={`type-total-${type}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ASSET_META[type].color }} />
              <span className="text-xs text-muted-foreground">{ASSET_META[type].label}</span>
            </div>
            <p className="text-lg font-medium text-foreground">{fmtCurrency(value, cur)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-serif text-xl text-foreground mb-4">Net worth over time</h2>
        <TrendChart snapshots={snapshots} getValue={(s) => s.total} color="#2C2C2C" currency={cur} testid="networth-trend" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-serif text-xl text-foreground mb-4">Allocation</h2>
          <AllocationPie data={allocation} currency={cur} testid="allocation-pie" />
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-serif text-xl text-foreground mb-4">Commodities breakdown</h2>
          <AllocationPie data={commodities} currency={cur} testid="commodities-pie" />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 overflow-x-auto">
        <h2 className="font-serif text-xl text-foreground mb-4">All holdings</h2>
        <table className="w-full text-sm" data-testid="holdings-table">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {summary.holdings.map((h) => (
              <tr key={h.id} className="border-b border-border/60 last:border-0">
                <td className="py-2 pr-4">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ASSET_META[h.type].color }} />
                    {ASSET_META[h.type].label}
                  </span>
                </td>
                <td className="py-2 pr-4 text-foreground">{h.name}</td>
                <td className="py-2 pr-4 text-right font-medium">
                  {h.value == null ? <span className="text-muted-foreground italic">unavailable</span> : fmtCurrency(h.value, cur)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
